"use client"

import * as React from "react"
import { ComposableMap, Geographies, Geography } from "react-simple-maps"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Users, MapPin, ArrowRight } from "lucide-react"
import Link from "next/link"

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json"

// Region definitions with state FIPS codes
const regions = {
  "northeast": {
    name: "Northeast",
    states: ["09", "23", "25", "33", "44", "50", "34", "36", "42"],
    stateNames: ["Connecticut", "Maine", "Massachusetts", "New Hampshire", "Rhode Island", "Vermont", "New Jersey", "New York", "Pennsylvania"],
    color: "hsl(180, 70%, 45%)",
    players: 420,
    leagues: 12
  },
  "southeast": {
    name: "Southeast",
    states: ["01", "12", "13", "21", "28", "37", "45", "47", "51", "54", "11", "24", "10"],
    stateNames: ["Alabama", "Florida", "Georgia", "Kentucky", "Mississippi", "North Carolina", "South Carolina", "Tennessee", "Virginia", "West Virginia", "DC", "Maryland", "Delaware"],
    color: "hsl(320, 70%, 50%)",
    players: 580,
    leagues: 15
  },
  "midwest": {
    name: "Midwest",
    states: ["17", "18", "26", "39", "55", "19", "20", "27", "29", "31", "38", "46"],
    stateNames: ["Illinois", "Indiana", "Michigan", "Ohio", "Wisconsin", "Iowa", "Kansas", "Minnesota", "Missouri", "Nebraska", "North Dakota", "South Dakota"],
    color: "hsl(45, 80%, 50%)",
    players: 390,
    leagues: 10
  },
  "southwest": {
    name: "Southwest",
    states: ["04", "35", "40", "48"],
    stateNames: ["Arizona", "New Mexico", "Oklahoma", "Texas"],
    color: "hsl(25, 80%, 55%)",
    players: 310,
    leagues: 8
  },
  "west": {
    name: "West",
    states: ["06", "08", "32", "49", "56"],
    stateNames: ["California", "Colorado", "Nevada", "Utah", "Wyoming"],
    color: "hsl(260, 60%, 55%)",
    players: 520,
    leagues: 14
  },
  "pacific-northwest": {
    name: "Pacific Northwest",
    states: ["16", "30", "41", "53", "02"],
    stateNames: ["Idaho", "Montana", "Oregon", "Washington", "Alaska"],
    color: "hsl(150, 60%, 45%)",
    players: 280,
    leagues: 7
  }
}

// Map state FIPS to region
const stateToRegion: Record<string, string> = {}
Object.entries(regions).forEach(([regionId, region]) => {
  region.states.forEach(state => {
    stateToRegion[state] = regionId
  })
})

export default function RegionsPage() {
  const [hoveredRegion, setHoveredRegion] = React.useState<string | null>(null)
  const [selectedRegion, setSelectedRegion] = React.useState<string | null>(null)

  const getRegionFromGeo = (geo: { id: string }) => {
    return stateToRegion[geo.id] || null
  }

  const handleStateHover = (geo: { id: string }) => {
    const region = getRegionFromGeo(geo)
    setHoveredRegion(region)
  }

  const handleStateClick = (geo: { id: string }) => {
    const region = getRegionFromGeo(geo)
    setSelectedRegion(region)
  }

  const activeRegion = selectedRegion || hoveredRegion

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10" />
          <div className="relative mx-auto max-w-6xl px-6 lg:px-8 py-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Our Regions
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We organize leagues regionally so you can connect with players nearby. Click a region to see available leagues.
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Map */}
            <div className="lg:col-span-2">
              <Card className="bg-card border-border overflow-hidden">
                <CardContent className="p-4">
                  <ComposableMap
                    projection="geoAlbersUsa"
                    projectionConfig={{ scale: 1000 }}
                    style={{ width: "100%", height: "auto" }}
                  >
                    <Geographies geography={geoUrl}>
                      {({ geographies }) =>
                        geographies.map((geo) => {
                          const regionId = getRegionFromGeo(geo)
                          const region = regionId ? regions[regionId as keyof typeof regions] : null
                          const isActive = activeRegion === regionId
                          
                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              onMouseEnter={() => handleStateHover(geo)}
                              onMouseLeave={() => setHoveredRegion(null)}
                              onClick={() => handleStateClick(geo)}
                              style={{
                                default: {
                                  fill: region ? region.color : "#374151",
                                  stroke: "#1f2937",
                                  strokeWidth: 0.5,
                                  opacity: isActive ? 1 : 0.7,
                                  outline: "none",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease"
                                },
                                hover: {
                                  fill: region ? region.color : "#4b5563",
                                  stroke: "#f3f4f6",
                                  strokeWidth: 1,
                                  opacity: 1,
                                  outline: "none",
                                  cursor: "pointer"
                                },
                                pressed: {
                                  fill: region ? region.color : "#4b5563",
                                  stroke: "#f3f4f6",
                                  strokeWidth: 1.5,
                                  opacity: 1,
                                  outline: "none"
                                }
                              }}
                            />
                          )
                        })
                      }
                    </Geographies>
                  </ComposableMap>
                </CardContent>
              </Card>

              {/* Legend */}
              <div className="mt-4 flex flex-wrap gap-3 justify-center">
                {Object.entries(regions).map(([id, region]) => (
                  <button
                    key={id}
                    onClick={() => setSelectedRegion(id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all ${
                      activeRegion === id 
                        ? "border-foreground bg-secondary" 
                        : "border-border hover:border-muted-foreground"
                    }`}
                  >
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: region.color }}
                    />
                    <span className="text-sm text-foreground">{region.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Region Info Panel */}
            <div>
              {activeRegion && regions[activeRegion as keyof typeof regions] ? (
                <Card className="bg-card border-border sticky top-24">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: regions[activeRegion as keyof typeof regions].color }}
                      />
                      <h2 className="text-xl font-bold text-foreground">
                        {regions[activeRegion as keyof typeof regions].name}
                      </h2>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-secondary rounded-lg p-4 text-center">
                        <Users className="h-5 w-5 mx-auto mb-1 text-primary" />
                        <p className="text-2xl font-bold text-foreground">
                          {regions[activeRegion as keyof typeof regions].players}
                        </p>
                        <p className="text-xs text-muted-foreground">Active Players</p>
                      </div>
                      <div className="bg-secondary rounded-lg p-4 text-center">
                        <MapPin className="h-5 w-5 mx-auto mb-1 text-accent" />
                        <p className="text-2xl font-bold text-foreground">
                          {regions[activeRegion as keyof typeof regions].leagues}
                        </p>
                        <p className="text-xs text-muted-foreground">Active Leagues</p>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-foreground mb-2">States Included</h3>
                      <div className="flex flex-wrap gap-1">
                        {regions[activeRegion as keyof typeof regions].stateNames.map(state => (
                          <Badge key={state} variant="secondary" className="text-xs">
                            {state}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <Button className="w-full bg-primary hover:bg-primary/90" asChild>
                      <Link href={`/leagues?regions=${activeRegion}`}>
                        View {regions[activeRegion as keyof typeof regions].name} Leagues
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-card border-border border-dashed">
                  <CardContent className="p-6 text-center">
                    <MapPin className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                    <h3 className="font-semibold text-foreground mb-2">Select a Region</h3>
                    <p className="text-sm text-muted-foreground">
                      Click on the map or legend to see region details and available leagues.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Total Stats */}
              <Card className="bg-card border-border mt-4">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Nationwide</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Players</span>
                      <span className="text-foreground font-medium">2,500+</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Active Leagues</span>
                      <span className="text-foreground font-medium">66</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Games Supported</span>
                      <span className="text-foreground font-medium">15+</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
